import React, { useEffect } from "react";

import {
  MeetingStatus,
  useMeetingManager,
} from "amazon-chime-sdk-component-library-react";

import { MeetingInfoContext } from "../../../context";

/**
 * MeetingStatusManager Component
 * 
 * This component monitors and handles Amazon Chime meeting status changes.
 * It subscribes to meeting status updates and manages the connection state
 * and error messages in the meeting context accordingly.
 * 
 * The component doesn't render any visible UI but acts as a status manager
 * that updates the meeting context based on various meeting states.
 */
const MeetingStatusManager: React.FC = () => {
  // Get the meeting manager from Amazon Chime SDK
  const meetingManager = useMeetingManager();

  // Extract meeting context values for managing connection state and error messages
  const { errorMessage, setErrorMessage, connected, setConnected } =
    React.useContext(MeetingInfoContext);

  // Track whether we've already set up the meeting status listener
  const [listenerSet, setListenerSet] = React.useState(false);

  useEffect(() => {
    /**
     * Handles meeting status changes and updates the context accordingly
     * @param status - The current meeting status from Amazon Chime SDK
     */
    const checkMeetingStatus = (status: MeetingStatus) => {
      // Handle successful connection - update connected state if not already connected
      if (status === MeetingStatus.Succeeded && connected) {
        return; // Already connected, no action needed
      }

      if (status === MeetingStatus.Succeeded && !connected) {
        setConnected(true);
      } else if (status !== MeetingStatus.Succeeded && connected) {
        // Meeting is no longer successful but we're marked as connected
        setConnected(false);
      }

      // Don't override existing error messages
      if (errorMessage) {
        return;
      }

      // Set appropriate error messages based on meeting status
      switch (status) {
        case MeetingStatus.Succeeded:
          setErrorMessage(null);
          break;

        case MeetingStatus.Failed:
          setErrorMessage("Failed to connect.");
          break;

        case MeetingStatus.Loading:
          setErrorMessage("Connecting...");
          break;

        case MeetingStatus.Ended:
          setErrorMessage("Meeting has ended.");
          break;

        case MeetingStatus.JoinedFromAnotherDevice:
          setErrorMessage("Joined from Another Device.");
          break;

        case MeetingStatus.Reconnecting:
          setErrorMessage("Reconnecting...");
          break;

        case MeetingStatus.TerminalFailure:
          setErrorMessage("Terminal Failure.");
          break;

        default:
          // Handle any unexpected status values
          break;
      }
    };

    // Early return if meetingManager is not available
    if (!meetingManager) {
      return;
    }

    // Prevent setting up multiple listeners
    if (listenerSet) {
      return;
    }

    // Subscribe to meeting status changes
    meetingManager.subscribeToMeetingStatus(checkMeetingStatus);

    // Mark listener as set to prevent duplicate subscriptions
    setListenerSet(true);
  }, [meetingManager, connected, errorMessage, setConnected, setErrorMessage, listenerSet]);

  // This component doesn't render any visible UI - it only manages meeting status
  return <></>;
};

export default MeetingStatusManager;
